import classNames from 'classnames';
import React__default, { useRef } from 'react';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import FileInput from './FileInput.js';
import Image from './Image.js';
import PlusIcon from '../Icons/PlusIcon.js';
import ReplaceIcon from '../Icons/ReplaceIcon.js';
import TrashIcon from '../Icons/TrashIcon.js';
import LoaderIcon from '../Icons/LoaderIcon.js';
import '@wix/image-kit';
import 'fastdom';
import './MediaItemErrorMsg.js';
import 'wix-rich-content-common';
import '../Icons/ErrorIcon.js';

var css_248z = ".-rWVu{box-sizing:border-box;display:inline-block;overflow:hidden;position:relative;vertical-align:top}[dir] .-rWVu{border-radius:var(--ricos-settings-whitebox-border-radius,0)}[dir] .-rWVu:not(.Pxk8a){margin:6px}[dir] .-rWVu.g3v8V{box-shadow:0 0 0 3px var(--ricos-settings-action-color,var(--ricos-action-color-fallback,#116dff))}.wjLaJ{align-items:center;box-sizing:border-box;display:flex;justify-content:center;overflow:hidden;position:relative}[dir] .wjLaJ{cursor:pointer}[dir] .wjLaJ:not(.MHMdi){border:1px dashed var(--ricos-settings-action-color,var(--ricos-action-color-fallback,#116dff))}.wjLaJ:after{display:none!important}.wjLaJ svg{fill:var(--ricos-settings-action-color,var(--ricos-action-color-fallback,#116dff));height:28px;width:28px}[dir] .wjLaJ:hover{background-color:rgba(var(--ricos-settings-action-color-tuple,var(--ricos-action-color-fallback-tuple,2,97,255)),.1)}.QOt4A{position:relative}.ci64O{align-items:center;display:flex;height:120px;justify-content:center;position:absolute;top:0;width:120px}[dir=ltr] .ci64O{left:0}[dir=rtl] .ci64O{right:0}.ci64O .xllfY{display:none;gap:6px;position:absolute;z-index:2}.ci64O .xllfY svg{fill:var(--ricos-settings-icons-color,currentColor);color:var(--ricos-settings-icons-color,currentColor);height:24px;width:24px}[dir] .ci64O .xllfY svg{background-color:#fff;border-radius:24px;cursor:pointer;padding:2px}.ci64O .FL4P0{height:120px;width:120px}[dir] .ci64O .FL4P0{border:1px solid var(--ricos-settings-inputs-border-color,rgba(0,0,0,.2));border-radius:var(--ricos-settings-whitebox-border-radius,0)}.ci64O:not(.MHMdi){z-index:-1}[dir] .ci64O.MHMdi:hover{background-color:#000}.ci64O.MHMdi:hover .xllfY{display:flex}.ci64O.MHMdi:hover .FL4P0{opacity:.6}.ci64O.H0xfc{z-index:1}[dir] .ci64O.H0xfc{background-color:rgba(0,0,0,.6)}.ci64O ._5MA4S{fill:#fff;color:#fff}[dir=ltr] .ci64O ._5MA4S{animation-duration:1.5s;animation-iteration-count:infinite;animation-name:MvlDL;animation-timing-function:linear}[dir=rtl] .ci64O ._5MA4S{animation-duration:1.5s;animation-iteration-count:infinite;animation-name:wcdNk;animation-timing-function:linear}@keyframes MvlDL{0%{transform:rotate(0deg)}to{transform:rotate(1turn)}}@keyframes wcdNk{0%{transform:rotate(0deg)}to{transform:rotate(-1turn)}}";
var styles = {"settingsAddItem":"-rWVu","withOverlay":"Pxk8a","itemContainerSelected":"g3v8V","filesItem":"wjLaJ","withImage":"MHMdi","settingsAddItem_container":"QOt4A","settingsAddItem_overlay":"ci64O","settingsAddItem_overlay_icons":"xllfY","settingsAddItem_overlay_image":"FL4P0","overlay_loading":"H0xfc","loader_icon":"_5MA4S","spin-ltr":"MvlDL","spin-rtl":"wcdNk"};
styleInject(css_248z);

const AddItemOverlay = ({
  handleReplace,
  handleDelete,
  src,
  size,
  alt,
  t,
  isLoading
}) => {
  return /* @__PURE__ */ React__default.createElement(
    "div",
    {
      className: classNames(styles.settingsAddItem_overlay, {
        [styles.withImage]: src,
        [styles.overlay_loading]: isLoading
      }),
      "data-hook": "addItemOverlay"
    },
    src && /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, /* @__PURE__ */ React__default.createElement("div", { className: styles.settingsAddItem_overlay_icons }, handleReplace && /* @__PURE__ */ React__default.createElement(Tooltip, { content: t("Settings_Replace_Image_Tooltip"), place: "top" }, /* @__PURE__ */ React__default.createElement(ReplaceIcon, { tabIndex: 0, onClick: handleReplace })), /* @__PURE__ */ React__default.createElement(Tooltip, { content: t("Settings_Remove_Image_Tooltip"), place: "top" }, /* @__PURE__ */ React__default.createElement(
      TrashIcon,
      {
        tabIndex: 0,
        onClick: handleDelete,
        "data-hook": "addItemOverlay-deleteIcon"
      }
    ))), !isLoading && !!src && !!alt && /* @__PURE__ */ React__default.createElement(
      Image,
      {
        id: src + alt,
        className: styles.settingsAddItem_overlay_image,
        uri: src,
        width: size.width,
        height: size.height,
        displayMode: "fill",
        alt,
        containerProps: {
          style: {
            width: size.width + "px",
            height: size.height + "px"
          }
        }
      }
    )),
    isLoading && /* @__PURE__ */ React__default.createElement(LoaderIcon, { className: styles.loader_icon })
  );
};
const SettingsAddItem = ({
  handleFileChange,
  isMobile,
  handleFileSelection,
  inlineStyle,
  uploadMediaLabel,
  accept,
  dataHook,
  handleDelete,
  size,
  src,
  alt,
  t,
  isLoading,
  hasReplace
}) => {
  const inputRef = useRef(null);
  const hasOverlay = size && handleDelete;
  return /* @__PURE__ */ React__default.createElement("div", { className: styles.settingsAddItem_container, "data-hook": "settingsAddItem_container" }, /* @__PURE__ */ React__default.createElement(
    FileInput,
    {
      className: classNames(styles.settingsAddItem, styles.filesItem, {
        [styles.mobile]: isMobile,
        [styles.withOverlay]: hasOverlay,
        [styles.withImage]: src
      }),
      dataHook,
      onChange: handleFileChange,
      handleFileSelection,
      tabIndex: src ? -1 : 0,
      multiple: true,
      title: uploadMediaLabel,
      style: inlineStyle,
      accept,
      ref: inputRef
    },
    /* @__PURE__ */ React__default.createElement(PlusIcon, null)
  ), hasOverlay && /* @__PURE__ */ React__default.createElement(
    AddItemOverlay,
    {
      handleDelete,
      src,
      alt,
      size,
      t,
      handleReplace: hasReplace ? () => {
        var _a;
        return (_a = inputRef.current) == null ? void 0 : _a.click();
      } : void 0,
      isLoading
    }
  ));
};

export { SettingsAddItem as default };
//# sourceMappingURL=SettingsAddItem.js.map
